import React from "react";
import { ModuleConfig } from "../../types/ModuleConfig";

const MyShelv = React.lazy(() => import("./MyShelv"));
const CaptureRecipe = React.lazy(() => import("./CaptureRecipe"));
const CaptureReview = React.lazy(() => import("./CaptureReview"));
const ShelvSettings = React.lazy(() => import("./ShelvSettings"));
const UiPreview = React.lazy(() => import("./UiPreview"));
const RecipeDetail = React.lazy(() => import("./RecipeDetail"));
const KitchenSetup = React.lazy(() => import("./KitchenSetup"));
const MealCalendar = React.lazy(() => import("./MealCalendar"));
const Budget = React.lazy(() => import("./Budget"));
const SupplierOrders = React.lazy(() => import("./SupplierOrders"));
const MealFeedback = React.lazy(() => import("./MealFeedback"));
const ShelvInvites = React.lazy(() => import("./ShelvInvites"));
const ShelvInviteFriend = React.lazy(() => import("./ShelvInviteFriend"));
const ShelvInviteGraph = React.lazy(() => import("./ShelvInviteGraph"));

// Not lazy: `withProGate` is called at module-config evaluation time, so the
// wrapper itself must be a real component, not a lazy promise.
import { withProGate } from "./components/ProGate";

/**
 * SHELV MODULE CONFIG
 *
 * SINGLE SOURCE OF TRUTH:
 * Permissions live ONCE in the backend (`shelv/permissions.ts`) and are fetched
 * via the `modulePermissions` query. The arrays below are EMPTY on purpose —
 * `ModulePermissionsContext` fills them at runtime.
 *
 * Permission levels:
 *   view   — read the kitchen's recipe book
 *   create — add recipes, capture from photo/paste/link
 *   edit   — edit recipes, manage kitchen members
 *   delete — delete recipes
 *   pro    — THE PAYWALL: calendar, budget, orders, feedback (SHELV_PRO,
 *            granted by the Shelv Pro SubscriptionProduct on activation)
 *   admin  — invites, settings, cross-kitchen
 *
 * Pro routes currently render `ProLocked`, which explains the tier and sends
 * the user to the one checkout path (`/subscribe/shelv-pro`). Swap in the real
 * pages as each is ported and walked by hand.
 */
const shelvModuleConfig: ModuleConfig = {
    id: "shelv",
    name: "Shelv",
    description: "Your restaurant's recipe book — capture recipes from a photo, a link or pasted text and keep them in one standard format",
    version: "1.0.0",
    enabled: true,
    category: "operations",
    order: 1,
    icon: "📚",
    color: "#2F6F4E",

    routes: [
        { path: "/shelv", component: MyShelv, permissionLevel: "view", useLayout: false },
        { path: "/shelv/capture", component: CaptureRecipe, permissionLevel: "create", useLayout: false },
        // The review screen for ONE capture job — where a "ready to check" card
        // on the shelf opens. Same teaser as /shelv/capture (prefix match).
        { path: "/shelv/capture/:jobId", component: CaptureReview, permissionLevel: "create", useLayout: false },
        // Where the three readers (recipe / photo / web) are pointed. Admin
        // only, and hidden — not disabled — from everyone else.
        { path: "/shelv/settings", component: ShelvSettings, permissionLevel: "admin", useLayout: false },
        // Every component in Ed's ui/ folder, every variant. Admin only, not in the nav.
        { path: "/shelv/ui", component: UiPreview, permissionLevel: "admin", useLayout: false },
        { path: "/shelv/kitchen", component: KitchenSetup, permissionLevel: "view", useLayout: true, layoutOptions: { noContainer: true } },
        { path: "/shelv/recipes/:id", component: RecipeDetail, permissionLevel: "view", useLayout: true, layoutOptions: { noContainer: true } },

        // ── Shelv Pro ────────────────────────────────────────────────────
        // Still gated at "view", not "pro", on purpose: a free user should REACH
        // the page and be told what Pro is, not hit an access-denied wall on a
        // nav item they can see. `ProGate` then decides which of the two they
        // get. The data itself is gated server-side on shelvPermissions.pro.
        //
        // ⚠️ These pointed straight at `ProLocked` until 2026-08-29, which meant
        // the explainer rendered for EVERYONE — a paying subscriber included.
        // Whatever sits here must stay wrapped in ProGate.
        { path: "/shelv/calendar", component: withProGate(MealCalendar), permissionLevel: "view", useLayout: true, layoutOptions: { noContainer: true } },
        { path: "/shelv/budget", component: withProGate(Budget), permissionLevel: "view", useLayout: true, layoutOptions: { noContainer: true } },
        { path: "/shelv/orders", component: withProGate(SupplierOrders), permissionLevel: "view", useLayout: true, layoutOptions: { noContainer: true } },
        { path: "/shelv/feedback", component: withProGate(MealFeedback), permissionLevel: "view", useLayout: true, layoutOptions: { noContainer: true } },

        // ── Invites ──────────────────────────────────────────────────────
        // The public landing page (/shelv/invite/:slug) is NOT here — it is a
        // bare top-level route in App.tsx, because it has no login and this
        // array assumes permissionLevel gating.
        { path: "/shelv/invites", component: ShelvInvites, permissionLevel: "admin", useLayout: true, layoutOptions: { noContainer: true } },
        { path: "/shelv/invite-a-friend", component: ShelvInviteFriend, permissionLevel: "create", useLayout: true, layoutOptions: { noContainer: true } },
        { path: "/shelv/invite-graph", component: ShelvInviteGraph, permissionLevel: "admin", useLayout: true, layoutOptions: { noContainer: true } },
    ],

    navigation: [
        { label: "The Shelf", path: "/shelv", icon: "📚", permissionLevel: "view" },
        { label: "Add a Recipe", path: "/shelv/capture", icon: "➕", permissionLevel: "create" },
        { label: "Kitchen", path: "/shelv/kitchen", icon: "👨‍🍳", permissionLevel: "view" },
        { label: "Meal Calendar", path: "/shelv/calendar", icon: "🗓️", permissionLevel: "view" },
        { label: "Budget", path: "/shelv/budget", icon: "💰", permissionLevel: "view" },
        { label: "Supplier Orders", path: "/shelv/orders", icon: "🚚", permissionLevel: "view" },
        { label: "Feedback", path: "/shelv/feedback", icon: "⭐", permissionLevel: "view" },
        { label: "Invite a friend", path: "/shelv/invite-a-friend", icon: "✉️", permissionLevel: "create" },
        { label: "Invites", path: "/shelv/invites", icon: "📨", permissionLevel: "admin" },
        { label: "Invite graph", path: "/shelv/invite-graph", icon: "🕸️", permissionLevel: "admin" },
        { label: "Shelv settings", path: "/shelv/settings", icon: "⚙️", permissionLevel: "admin" },
    ],

    // Backend is the source of truth — see shelv/permissions.ts.
    // These get replaced at runtime by ModulePermissionsContext.
    permissions: {
        view: [],
        create: [],
        edit: [],
        delete: [],
    },
};

export default shelvModuleConfig;
